import { Component, type ErrorInfo, type ReactNode } from 'react';
import { Link, isRouteErrorResponse, useRouteError } from 'react-router-dom';
import { Ban, FileQuestion, Power, TriangleAlert } from 'lucide-react';
import { Button } from '@/components/ui';
import { HOME_PATH, type FeatureFlag } from '@/routes/registry';

function Centered({
  icon,
  title,
  children,
  actions,
}: {
  icon: ReactNode;
  title: string;
  children?: ReactNode;
  actions?: ReactNode;
}) {
  return (
    <div className="flex min-h-[60vh] flex-col items-center justify-center gap-3 p-6 text-center">
      <div className="text-fg-subtle [&>svg]:size-14">{icon}</div>
      <h1 className="text-2xl font-bold text-fg">{title}</h1>
      {children && <p className="max-w-md text-fg-muted">{children}</p>}
      {actions && <div className="mt-2 flex gap-2">{actions}</div>}
    </div>
  );
}

const homeLink = (
  <Link
    to={HOME_PATH}
    className="inline-flex h-10 items-center rounded-lg bg-primary px-4 text-sm font-medium text-primary-fg hover:bg-primary-hover"
  >
    Back to POS
  </Link>
);

export function NotFoundPage() {
  return (
    <Centered icon={<FileQuestion aria-hidden />} title="Page not found" actions={homeLink}>
      The page you are looking for does not exist or has moved.
    </Centered>
  );
}

export function ForbiddenPage() {
  return (
    <Centered icon={<Ban aria-hidden />} title="Access denied" actions={homeLink}>
      Your account does not have permission to open this page.
    </Centered>
  );
}

const FEATURE_LABEL: Record<FeatureFlag, string> = {
  rooms_enabled: 'Rooms',
  shifts_enabled: 'Shifts',
};

export function FeatureDisabledPage({ feature }: { feature: FeatureFlag }) {
  return (
    <Centered
      icon={<Power aria-hidden />}
      title={`${FEATURE_LABEL[feature]} is turned off`}
      actions={homeLink}
    >
      This feature is not enabled for your business. A super admin can turn it on in Settings.
    </Centered>
  );
}

/** react-router `errorElement`: render/loader errors and unmatched routes thrown as responses. */
export function RouteErrorPage() {
  const error = useRouteError();
  if (isRouteErrorResponse(error) && error.status === 404) return <NotFoundPage />;
  const message = error instanceof Error ? error.message : 'Something went wrong.';
  return (
    <Centered
      icon={<TriangleAlert aria-hidden />}
      title="Something went wrong"
      actions={
        <>
          <Button onClick={() => window.location.reload()}>Reload</Button>
          {homeLink}
        </>
      }
    >
      {message}
    </Centered>
  );
}

interface BoundaryState {
  error: Error | null;
}

/**
 * Wrap a screen so a render crash shows a recoverable message instead of a blank app.
 * The app shell already wraps every routed screen; use it again around risky sub-panels.
 * Pass `resetKey` (e.g. the pathname) to clear the error on navigation.
 */
export class ErrorBoundary extends Component<
  { children: ReactNode; resetKey?: string },
  BoundaryState
> {
  state: BoundaryState = { error: null };

  static getDerivedStateFromError(error: Error): BoundaryState {
    return { error };
  }

  componentDidCatch(error: Error, info: ErrorInfo) {
    console.error('UI crash:', error, info.componentStack);
  }

  componentDidUpdate(prev: { resetKey?: string }) {
    if (this.state.error && prev.resetKey !== this.props.resetKey) this.setState({ error: null });
  }

  render() {
    if (!this.state.error) return this.props.children;
    return (
      <Centered
        icon={<TriangleAlert aria-hidden />}
        title="This screen crashed"
        actions={<Button onClick={() => this.setState({ error: null })}>Try again</Button>}
      >
        {this.state.error.message}
      </Centered>
    );
  }
}
